import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PostHogProvider } from "posthog-js/react";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import {
  enableAnalytics,
  envMode,
  POSTHOG_API_KEY,
  POSTHOG_HOST,
} from "./api/index.ts";
import App from "./App.tsx";
import ChatSocketProvider from "./helpers/utils/chatSocketProvider/ChatSocketProvider.tsx";
import "./index.css";
import { persistor, store } from "./redux-store/store.ts";

Sentry.init({
  dsn: "https://3197b51f283407b60ab23915ae319f1f@o4509394058018816.ingest.de.sentry.io/4509400583438416",
  integrations: [Sentry.browserTracingIntegration()],
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  tracesSampleRate: 0.1,
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/mobile-api.mymdoc.com/,
    /^https:\/\/api.navihealth.ai/,
    /^https:\/\/kem-backend-staging-preifqquja-ue.a.run.app/,
  ],
  enabled: enableAnalytics,
  environment: envMode,
  ignoreErrors: [
    "Request failed with status code 401",
    "messaging/unsupported-browser",
  ],
});

const queryClient = new QueryClient();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <PostHogProvider
            apiKey={POSTHOG_API_KEY}
            options={{
              api_host: POSTHOG_HOST,
              autocapture: false,
              capture_pageview: false,
              capture_pageleave: true,
              capture_dead_clicks: true,
              rageclick: true,
              disable_session_recording: !enableAnalytics,
              session_recording: {
                sampleRate: 0.1,
              },
              loaded: (posthog) => {
                if (!enableAnalytics) {
                  posthog.opt_out_capturing();
                }
                // fetch feature flags as early as possible on load
                posthog.reloadFeatureFlags?.();
              },
            }}
          >
            <ChatSocketProvider>
              <App />
            </ChatSocketProvider>
          </PostHogProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
);
